import { useEffect, useState } from "react";
import { Loader2, Users, GraduationCap, Award, TrendingUp, HelpCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/lms/analytics");
        setData(res.data);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-[var(--muted)] text-sm"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading analytics…</div>;
  if (error) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3">{error}</div>;
  if (!data) return null;

  const t = data.totals || {};
  return (
    <div data-testid="analytics-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Kpi icon={<GraduationCap size={14} />} label="Courses" value={t.courses} />
        <Kpi icon={<Users size={14} />} label="Unique Learners" value={t.unique_learners} />
        <Kpi icon={<TrendingUp size={14} />} label="Enrollments" value={t.enrollments} />
        <Kpi icon={<Award size={14} />} label="Certificates Earned" value={t.certificates_earned} highlight />
        <Kpi icon={<HelpCircle size={14} />} label="Quiz Attempts" value={t.quiz_attempts} />
        <Kpi label="Quiz Pass Rate" value={t.quiz_attempts ? `${Math.round((t.quiz_pass_rate || 0) * 100)}%` : "—"} />
        <Kpi label="Lesson Completions" value={t.lesson_completions} />
      </div>

      <div className="border border-[var(--line)] bg-white overflow-x-auto" data-testid="courses-analytics-table">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cream)] text-[10px] tracking-widest uppercase text-[var(--muted)]">
            <tr>
              <th className="text-left px-4 py-3">Course</th>
              <th className="text-right px-4 py-3">Lessons</th>
              <th className="text-right px-4 py-3">Enrolled</th>
              <th className="text-right px-4 py-3">In Progress</th>
              <th className="text-right px-4 py-3">Completed</th>
              <th className="text-right px-4 py-3">Avg %</th>
              <th className="text-right px-4 py-3">Completion Rate</th>
            </tr>
          </thead>
          <tbody>
            {(data.courses || []).map((c) => (
              <tr key={c.product_id} className="border-t border-[var(--line)]">
                <td className="px-4 py-3 font-bold text-[var(--navy)]">{c.name}</td>
                <td className="px-4 py-3 text-right">{c.total_lessons}</td>
                <td className="px-4 py-3 text-right">{c.enrolled}</td>
                <td className="px-4 py-3 text-right">{c.in_progress}</td>
                <td className="px-4 py-3 text-right">{c.completed}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-[var(--line)] overflow-hidden">
                      <div className="h-full bg-[var(--gold)]" style={{ width: `${c.avg_percent}%` }} />
                    </div>
                    <span>{c.avg_percent}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-bold text-[var(--navy)]">{c.completion_rate}%</td>
              </tr>
            ))}
            {(!data.courses || data.courses.length === 0) && (
              <tr><td className="px-4 py-8 text-center text-[var(--muted)]" colSpan={7}>No course data yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, highlight }) {
  return (
    <div className={`border p-4 ${highlight ? "border-[var(--gold)] bg-[var(--cream)]" : "border-[var(--line)] bg-white"}`}>
      <div className="text-[10px] tracking-widest uppercase text-[var(--muted)] flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="font-display text-2xl text-[var(--navy)] mt-1">{value ?? 0}</div>
    </div>
  );
}
